import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { getModalPortalTarget } from '../lib/portalTarget';
import { EditBar } from './EditBar';
import { SaveFeedback } from './SaveFeedback';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import { useEditSession } from './useEditSession';
import { useGuardedClose } from './useGuardedClose';

const WIDTH: Record<'md' | 'lg' | 'xl', string> = { md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' };

export interface DocumentDialogProps<T extends object> {
  open: boolean;
  title: string;
  /** Für Rückfragen und Meldungen, z. B. „Aufgabe" */
  label: string;
  /** Eindeutig je Dokument, z. B. `task:<id>`; bei Neuanlage `task:new` */
  id: string;
  /** Geladenes Dokument; null, solange geladen wird */
  baseline: T | null;
  /**
   * Standard read (Entscheid E5). edit für Neuanlage und für Seiten, die selbst schon die
   * Anzeige des Dokuments sind.
   */
  startIn?: 'read' | 'edit';
  canEdit?: boolean;
  /** Nach „Speichern": read = zurück in die Anzeige (E5), close = Dialog schließt (Neuanlage) */
  afterSave?: 'read' | 'close';
  validate?: (value: T) => string[];
  onSave: (value: T, changed: Array<keyof T>) => Promise<T | void>;
  /** Nach erfolgreichem Speichern, z. B. Liste neu laden */
  onChanged?: (value: T) => void;
  onClose: () => void;
  renderRead: (value: T) => ReactNode;
  renderEdit: (value: T, setField: <K extends keyof T>(key: K, v: T[K]) => void) => ReactNode;
  /** Weitere Knöpfe im Lesemodus, z. B. „Löschen" */
  readActions?: ReactNode;
  size?: 'md' | 'lg' | 'xl';
  testAnchor?: string;
}

/**
 * Dialog für ein ganzes Dokument (Entscheid E5): öffnet in der Anzeige, „Bearbeiten"
 * schaltet um, „Speichern" führt zurück in die Anzeige, „Abbrechen" schließt den Dialog.
 * Klick daneben, X und Escape fragen bei Änderungen nach.
 */
export function DocumentDialog<T extends object>(props: DocumentDialogProps<T>) {
  const { open, title, label, id, baseline, startIn = 'read', canEdit = true, afterSave = 'read', size = 'lg', testAnchor } = props;

  const session = useEditSession<T>({
    id: `dialog:${id}`,
    label,
    baseline,
    initialMode: startIn,
    canEdit,
    validate: props.validate,
    onSave: async (value, changed) => {
      const result = await props.onSave(value, changed);
      props.onChanged?.(result ?? value);
      if (afterSave === 'close') props.onClose();
      return result;
    },
    // Der Dialog meldet sich über useGuardedClose an — sonst fragte der Verlassen-Schutz doppelt.
    guard: false,
    onLeave: props.onClose,
    // Neuanlage: „Speichern“ ohne Eingabe muss prüfen und anlegen, nicht nur den Modus wechseln.
    saveWhenClean: afterSave === 'close',
  });

  const { requestClose, confirmProps } = useGuardedClose({
    open,
    dirty: session.dirty,
    busy: session.saving,
    label,
    onClose: () => { session.discard(); props.onClose(); },
  });

  // Bleibt der Dialog eingehängt, beginnt jedes Öffnen wieder im vorgesehenen Modus.
  const { startEdit } = session;
  useEffect(() => {
    if (open && startIn === 'edit') startEdit();
  }, [open, startIn, startEdit]);

  if (!open) return null;
  const value = session.value;

  return createPortal(
    <>
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 sm:p-4" onClick={requestClose} data-test-anchor={testAnchor}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-mode={session.mode}
        className={`bg-slate-900 border border-slate-800 rounded-xl w-full ${WIDTH[size]} max-h-[92dvh] flex flex-col shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800 flex-shrink-0">
          <h3 className="font-semibold text-slate-100 min-w-0 truncate">{title}</h3>
          <button
            type="button"
            onClick={requestClose}
            disabled={session.saving}
            aria-label="Schließen"
            className="text-slate-400 hover:text-slate-200 min-h-[44px] min-w-[44px] flex items-center justify-center disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-4">
          {value == null
            ? <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 size={20} className="animate-spin" /></div>
            : session.editing ? props.renderEdit(value, session.setField) : props.renderRead(value)}
        </div>
        <div className="p-4 border-t border-slate-800 flex-shrink-0">
          <EditBar
            editing={session.editing}
            dirty={session.dirty}
            saving={session.saving}
            canEdit={canEdit && value != null}
            onEdit={session.startEdit}
            onSave={() => { void session.save(); }}
            onCancel={requestClose}
            extra={session.editing ? undefined : (
              <>
                {props.readActions}
                <button type="button" onClick={requestClose} className="inline-flex items-center justify-center px-4 py-2 rounded-lg min-h-[44px] text-sm text-slate-300 hover:bg-slate-800" data-action="close">
                  Schließen
                </button>
              </>
            )}
          />
        </div>
      </div>
    </div>
    <UnsavedChangesModal {...confirmProps} />
    <SaveFeedback label={label} error={session.error} clearError={session.clearError} issues={session.issues} clearIssues={session.clearIssues} />
    </>,
    getModalPortalTarget(),
  );
}
